import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '../config/firebase';
import { getJobApplications } from '../services/applicationService';
import { getJob } from '../services/jobService';
import { getCurrentUser } from '../services/authService';
import { isPermissionDenied, fetchPublicWorkerName } from '../services/publicWorkerProfileService';
import I9DatePicker from '../components/I9DatePicker';
import RateWorkerModal from '../components/RateWorkerModal';
import WorkerHeatMap from '../components/WorkerHeatMap';
import { useWindowSize, BP } from '../hooks/useWindowSize';
import { computeRatingEligibility } from '../utils/ratingEligibility';
import { fetchPublicProfileDomain } from '../services/platformConfigService';
import { publicProfileUrl } from '../utils/publicProfileLink';
import { checkOfferDates, OFFER_DATE_MESSAGES } from '../utils/offerDates';

const LANG_LABELS = { es: 'Español (Spanish)', zh: '中文简体 (Chinese Simplified)', 'zh-TW': '中文繁體 (Chinese Traditional)', ko: '한국어 (Korean)', ja: '日本語 (Japanese)' };

function ViewApplicantsPage() {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const { width } = useWindowSize();
  const isMobile = width < BP.mobile;

  const [job, setJob] = useState(null);
  const [applicants, setApplicants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(null);
  const [offerModal, setOfferModal] = useState(null); // { applicationId, offerId?, draft?, fields, loading, error, sent }
  const [sentOffers, setSentOffers] = useState({}); // { [applicationId]: true }
  const [offerWarningModal, setOfferWarningModal] = useState(null); // { applicationId, currentStatus, rejectedReason }
  const [resendReason, setResendReason] = useState('');
  const [resendOfferError, setResendOfferError] = useState(null);
  const [i9RequestMap, setI9RequestMap] = useState({}); // { [workerId]: { id, workbrightStatus } }
  const [i9Modal, setI9Modal] = useState(null); // { workerId, i9RequestId, workerName }
  const [posterLoading, setPosterLoading] = useState(false);
  const [posterLanguage, setPosterLanguage] = useState('en');
  // HC-RATING-004: { [applicationId]: boolean } — client-side display state
  // only, mirrors submitRating's own offer_letters lookup; not the
  // authoritative gate (see utils/ratingEligibility.js).
  const [ratingEligibilityMap, setRatingEligibilityMap] = useState({});
  const [rateModal, setRateModal] = useState(null); // { applicationId, workerName }
  // Locally tracks applications the employer just rated in this session, so
  // the Rate action disappears immediately without waiting on a full
  // applicants reload — mirrors mobile's _optimisticallyRated flag.
  const [locallyRated, setLocallyRated] = useState({}); // { [applicationId]: true }
  // QA d (Prompt #591, T138 (1)): the applicant-profile link's host comes from
  // config/platform.publicProfileDomain; null (missing/unreadable) hides the link.
  const [publicProfileDomain, setPublicProfileDomain] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const d = await fetchPublicProfileDomain();
      if (!cancelled) setPublicProfileDomain(d ?? null);
    })();
    return () => { cancelled = true; };
  }, []);

  // ── Poster helper ─────────────────────────────────────────────────────────
  const handleGeneratePoster = async () => {
    setPosterLoading(true);
    try {
      const fns = getFunctions();
      const generateJobPosterFn = httpsCallable(fns, 'generateJobPoster');
      const result = await generateJobPosterFn({ jobId, language: posterLanguage });
      const { posterUrlLetter, posterUrlA4 } = result.data;
      setJob(prev => ({ ...prev, posterUrl: { letter: posterUrlLetter, a4: posterUrlA4 } }));
    } catch (err) {
      console.error('generateJobPoster error:', err);
      alert('Failed to generate poster. Please try again.');
    } finally {
      setPosterLoading(false);
    }
  };

  // ── Offer Letter helpers ─────────────────────────────────────────────────

  const openOfferModal = async (applicationId) => {
    setOfferModal({ applicationId, loading: true, error: null, sent: false, fields: {} });
    try {
      const functions = getFunctions();
      const generateOfferLetter = httpsCallable(functions, 'generateOfferLetter');
      const result = await generateOfferLetter({ applicationId });
      const { offerId, draft } = result.data;
      setOfferModal({
        applicationId,
        offerId,
        draft,
        loading: false,
        error: null,
        sent: false,
        fields: {
          jobTitle: draft.jobTitle || '',
          company: job?.companyName || job?.company || draft.company || '',
          salary: (() => {
            if (job?.salaryMin != null && job?.salaryMax != null) return `$${job.salaryMin}-$${job.salaryMax}`;
            if (job?.salaryMin != null) return `From $${job.salaryMin}`;
            return draft.salary || job?.salary || '';
          })(),
          location: (job?.city && job?.state) ? `${job.city}, ${job.state}` : (draft.location || job?.location || ''),
          employerName: draft.employerName || '',
          workerName: draft.workerName || '',
          startDate: job?.startDate ? `${job.startDate}T09:00` : '',
          endDate: job?.endDate ? `${job.endDate}T09:00` : 'TBD',
          additionalTerms: '',
        },
      });
    } catch (err) {
      setOfferModal((prev) => ({ ...prev, loading: false, error: err.message || 'Failed to generate offer letter' }));
    }
  };

  const updateOfferField = (field, value) => {
    setOfferModal((prev) => ({ ...prev, fields: { ...prev.fields, [field]: value } }));
  };

  const sendOffer = async () => {
    if (!offerModal?.offerId) return;
    // QA a (Prompt #591): UX only; sendOfferLetter refuses the same dates on the server.
    if (checkOfferDates(offerModal.fields?.startDate, offerModal.fields?.endDate)) return;
    setOfferModal((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const functions = getFunctions();
      const sendOfferLetter = httpsCallable(functions, 'sendOfferLetter');
      const result = await sendOfferLetter({
        offerId: offerModal.offerId,
        ...offerModal.fields,
        endDate: offerModal.fields.endDate || 'TBD',
      });
      const pdfUrl = result.data?.pdfUrl;
      // status: 'offer_sent' is now written server-side by sendOfferLetter itself
      // (HC-SECURITY-004) — this optimistic local update reflects the CF's
      // response, not a separate client write.
      setSentOffers((prev) => ({ ...prev, [offerModal.applicationId]: pdfUrl || true }));
      setApplicants((prev) => prev.map((app) =>
        app.id === offerModal.applicationId ? { ...app, status: 'offer_sent' } : app
      ));
      setOfferModal((prev) => ({ ...prev, loading: false, sent: true }));
    } catch (err) {
      setOfferModal((prev) => ({ ...prev, loading: false, error: err.message || 'Failed to send offer letter' }));
    }
  };

  useEffect(() => {
    let cancelled = false;

    // Job doc itself isn't part of the staleness gap this fix addresses
    // (HC-EMPLOYER-FREEZE-001) — kept as a one-time load, unchanged.
    getJob(jobId)
      .then((jobData) => { if (!cancelled) setJob(jobData); })
      .catch((error) => console.error('Error loading job:', error));

    // HC-EMPLOYER-FREEZE-001: real-time listener (was a one-time getDocs via
    // getJobApplicationsOnce) so a new application arriving, a status change
    // made elsewhere (mobile, another employer session), or aiScore/aiLabel
    // landing after onApplicationCreated's async AI screening completes all
    // reflect on this page without a manual reload. The callback re-runs the
    // same derived-data lookups (user details, sent-offer pdfUrls, i9 map,
    // rating eligibility) on every snapshot, same as the original one-shot
    // load — those are all keyed off applicant status and would otherwise
    // grow stale independently of the raw applicants list.
    // Prompt #451: every query below is constrained on employerId — the
    // read-exposure fix gates applications/offer_letters/i9_requests to the
    // owning employer, and rules can only allow a query they can prove.
    const employerId = getCurrentUser()?.uid;
    const unsubscribe = getJobApplications(jobId, employerId, async (applicantsData) => {
      try {
        // Fetch user details for each applicant
        const applicantsWithUsers = await Promise.all(
          applicantsData.map(async (applicant) => {
            const userDetails = await fetchUserDetails(applicant.userId);
            return { ...applicant, userDetails };
          })
        );

        // Load pdfUrls for applicants that already have an offer letter sent
        const offerSnaps = await Promise.all(
          applicantsData
            .filter(a => a.status === 'offer_sent' || a.status === 'hired')
            .map(async (a) => {
              const offerQuery = await getDocs(
                query(collection(db, 'offer_letters'),
                  where('applicationId', '==', a.id),
                  where('status', '==', 'sent'),
                  where('employerId', '==', employerId)
                )
              );
              if (!offerQuery.empty) {
                return { applicationId: a.id, pdfUrl: offerQuery.docs[0].data().pdfUrl };
              }
              return null;
            })
        );
        const existingSentOffers = {};
        offerSnaps.filter(Boolean).forEach(o => {
          existingSentOffers[o.applicationId] = o.pdfUrl || true;
        });

        // Build workerId → i9RequestId map for hired applicants
        const i9Snap = await getDocs(
          query(collection(db, 'i9_requests'), where('jobId', '==', jobId), where('employerId', '==', employerId))
        );
        const i9Map = {};
        i9Snap.forEach(d => {
          const data = d.data();
          i9Map[data.workerId] = { id: d.id, workbrightStatus: data.workbrightStatus };
        });

        // HC-RATING-004: client-side rating-eligibility display state for
        // hired, not-yet-rated applicants — mirrors submitRating's own
        // offer_letters lookup exactly (query by applicationId, prefer the
        // accepted doc, else the first match) so the "Rate" action only
        // appears once the same window submitRating itself enforces has
        // actually opened. Not the authoritative gate — submitRating
        // re-validates server-side regardless.
        const eligibilitySnaps = await Promise.all(
          applicantsData
            .filter(a => a.status === 'hired' && !a.hasEmployerRating)
            .map(async (a) => {
              const offerQuery = await getDocs(
                query(collection(db, 'offer_letters'), where('applicationId', '==', a.id), where('employerId', '==', employerId))
              );
              if (offerQuery.empty) return null;
              const accepted = offerQuery.docs.find(d => d.data().workerResponse === 'accepted');
              const offerData = (accepted || offerQuery.docs[0]).data();
              const eligibility = computeRatingEligibility({
                startDate: offerData.startDate,
                endDate: offerData.endDate,
              });
              return { applicationId: a.id, eligible: eligibility.eligible };
            })
        );
        const eligibilityMap = {};
        eligibilitySnaps.filter(Boolean).forEach(r => {
          eligibilityMap[r.applicationId] = r.eligible;
        });

        if (cancelled) return;
        setSentOffers(existingSentOffers);
        setI9RequestMap(i9Map);
        setRatingEligibilityMap(eligibilityMap);
        setApplicants(applicantsWithUsers);
      } catch (error) {
        console.error('Error loading data:', error);
      } finally {
        if (!cancelled) setLoading(false);
      }
    });

    return () => {
      cancelled = true;
      unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId]);

  // HC-REFERENCES (Prompt #224/#225) — references live in
  // users/{workerId}/private/data, which firestore.rules has no employer
  // read branch for at all; this callable is the only access path, and it
  // does its own authorization check (self / platform / employer with a
  // real application from this worker). Own try/catch so a failure here
  // never blanks out the rest of fetchUserDetails's already-successful data.
  const fetchWorkerReferences = async (workerId) => {
    try {
      const functions = getFunctions();
      const getWorkerReferencesFn = httpsCallable(functions, 'getWorkerReferences');
      const result = await getWorkerReferencesFn({ workerId });
      return result.data?.references || [];
    } catch (error) {
      console.error('Error fetching worker references:', error);
      return [];
    }
  };

  // HC-INFRA-008C, Prompt #422 — reads the worker's contact details via the
  // relationship-verified getWorkerContactDetails callable (Prompt #415's
  // shared verifyEmployerWorkerRelationship gate — same authorization shape
  // fetchWorkerReferences above already uses) instead of a direct
  // users/{uid} read. This page renders email directly (the applicant
  // table/detail views below), so it needs the one callable that's actually
  // allowed to return it — a raw doc read is no longer this page's own
  // source of truth for worker PII. Run in parallel with the references
  // fetch (both are independent per-worker lookups); the callable's own
  // permission-denied (a relationship that no longer qualifies) is handled
  // by the same outer try/catch already used for any other failure here —
  // falls back to the existing "Unknown User" placeholder, never crashes.
  const fetchUserDetails = async (userId) => {
    try {
      const functions = getFunctions();
      const getWorkerContactDetailsFn = httpsCallable(functions, 'getWorkerContactDetails');
      const [contactResult, references] = await Promise.all([
        getWorkerContactDetailsFn({ workerId: userId }),
        fetchWorkerReferences(userId),
      ]);
      const contact = contactResult.data || {};
      return {
        name: contact.displayName || contact.email?.split('@')[0] || 'Unknown User',
        email: contact.email || 'No email',
        photoURL: contact.photoURL || null,
        avatarId: contact.avatarId || null,
        references,
      };
    } catch (error) {
      console.error('Error fetching user:', error);
      // Bug A (T19, Prompt #520): on permission-denied, the name (only) comes
      // from the public projection; email stays denied. Never users/{uid}.
      const name = isPermissionDenied(error) ? await fetchPublicWorkerName(userId) : null;
      return { name: name || 'Unknown User', email: 'No email', photoURL: null, avatarId: null, references: [] };
    }
  };

  const updateStatus = async (applicationId, newStatus) => {
    setUpdating(applicationId);
    try {
      const functions = getFunctions();
      const updateApplicationStatusFn = httpsCallable(functions, 'updateApplicationStatus');
      await updateApplicationStatusFn({ applicationId, newStatus });

      // Update local state
      setApplicants(prev => prev.map(app =>
        app.id === applicationId ? { ...app, status: newStatus } : app
      ));
    } catch (error) {
      console.error('Error updating status:', error);
      alert('Failed to update status');
    } finally {
      setUpdating(null);
    }
  };

  const handleStatusChange = (applicationId, newStatus) => {
    if (newStatus === 'send_offer') {
      const applicant = applicants.find(a => a.id === applicationId);
      setResendReason('');
      setResendOfferError(null);
      setOfferWarningModal({
        applicationId,
        currentStatus: applicant?.status,
        rejectedReason: applicant?.rejectedReason ?? null,
      });
    } else {
      updateStatus(applicationId, newStatus);
    }
  };

  const confirmSendOffer = async () => {
    if (!offerWarningModal) return;
    const { applicationId, currentStatus } = offerWarningModal;

    if (currentStatus === 'rejected') {
      // Rejected → send_offer: must go through resendOffer CF
      setResendOfferError(null);
      try {
        const functions = getFunctions();
        const resendOfferFn = httpsCallable(functions, 'resendOffer');
        await resendOfferFn({ applicationId, reason: resendReason });
        setApplicants(prev =>
          prev.map(app =>
            app.id === applicationId
              ? { ...app, status: 'send_offer', rejectedReason: null }
              : app
          )
        );
        setOfferWarningModal(null);
      } catch (error) {
        setResendOfferError(error.message || 'Failed to re-send offer');
      }
    } else {
      // Non-rejected → send_offer: routed through updateApplicationStatus CF
      // (HC-SECURITY-004 — applications.status is no longer client-writable)
      try {
        const functions = getFunctions();
        const updateApplicationStatusFn = httpsCallable(functions, 'updateApplicationStatus');
        await updateApplicationStatusFn({ applicationId, newStatus: 'send_offer' });
        setApplicants(prev =>
          prev.map(app =>
            app.id === applicationId ? { ...app, status: 'send_offer' } : app
          )
        );
        setOfferWarningModal(null);
      } catch (error) {
        console.error('Error updating status:', error);
        alert('Failed to update status');
      }
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return '#f59e0b';
      case 'reviewing': return '#3b82f6';
      case 'hired': return '#10b981';
      case 'offer_sent': return '#0f766e';
      case 'send_offer': return '#7c3aed';
      case 'rejected': return '#ef4444';
      default: return '#6b7280';
    }
  };

  // HC-EMPLOYER-004b: canceled jobs are also terminal — reuse the same gate
  // that already disables the status dropdown and applicant actions for
  // closed/expired jobs, rather than duplicating a parallel check at every
  // one of this flag's call sites.
  const isJobClosed = job?.status === 'closed' || job?.status === 'expired' || job?.status === 'canceled';

  // HC-RATING-004: fail closed — until a real offer_letters read confirms
  // eligibility (ratingEligibilityMap[id] === true), the action stays
  // hidden rather than showing something that might immediately error.
  const canRateApplicant = (applicant) =>
    applicant.status === 'hired' &&
    !applicant.hasEmployerRating &&
    !locallyRated[applicant.id] &&
    ratingEligibilityMap[applicant.id] === true;

  const openRateModal = (applicant) => {
    setRateModal({ applicationId: applicant.id, workerName: applicant.userDetails?.name || 'Worker' });
  };

  const handleRatingSuccess = () => {
    if (rateModal) {
      setLocallyRated((prev) => ({ ...prev, [rateModal.applicationId]: true }));
    }
  };

  // QA d (Prompt #591): no link (plain name) when the domain is missing (fail closed).
  const renderApplicantName = (applicant) => {
    const name = applicant.userDetails?.name || 'Unknown User';
    const href = publicProfileUrl(publicProfileDomain, applicant.userId);
    if (!href) return name;
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        onMouseEnter={e => { e.currentTarget.style.textDecoration = 'underline'; }}
        onMouseLeave={e => { e.currentTarget.style.textDecoration = 'none'; }}
        style={{ color: '#E8590C', textDecoration: 'none' }}
      >
        {name}
      </a>
    );
  };

  const offerDateIssue = offerModal?.fields ? checkOfferDates(offerModal.fields.startDate, offerModal.fields.endDate) : null;
  const offerSendBlocked = offerModal?.loading || !offerModal?.fields?.startDate?.trim() || !!offerDateIssue;

  const stats = {
    total: applicants?.length || 0,
    pending: applicants?.filter(a => a.status === 'pending').length || 0,
    reviewing: applicants?.filter(a => a.status === 'reviewing').length || 0,
    send_offer: applicants?.filter(a => a.status === 'send_offer').length || 0,
    hired: applicants?.filter(a => a.status === 'hired').length || 0,
    offer_sent: applicants?.filter(a => a.status === 'offer_sent').length || 0,
    rejected: applicants?.filter(a => a.status === 'rejected').length || 0,
  };

  if (loading) {
    return <div style={{ padding: '2rem', textAlign: 'center' }}>Loading...</div>;
  }

  return (
    <div style={{ paddingBottom: '2rem', overflowX: 'hidden', maxWidth: '100vw' }}>
      {/* Best viewed on desktop notice — tablet only */}
      {!isMobile && width < BP.tablet && (
        <div style={{
          backgroundColor: '#fef3c7',
          border: '1px solid #f59e0b',
          borderRadius: '8px',
          padding: '12px 16px',
          marginBottom: '16px',
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          fontSize: '14px',
          color: '#92400e',
        }}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
          This page is best viewed on a desktop for full functionality.
        </div>
      )}
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingBottom: '1.25rem' }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
            <span
              onClick={() => navigate('/manage-jobs')}
              style={{ cursor: 'pointer', color: '#6b7280', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '4px' }}
              onMouseEnter={e => e.currentTarget.style.color = '#E8590C'}
              onMouseLeave={e => e.currentTarget.style.color = '#6b7280'}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M19 12H5M12 5l-7 7 7 7"/></svg>
              Manage Jobs
            </span>
            <span style={{ color: '#d1d5db', fontSize: '13px' }}>/</span>
            <span style={{ color: '#374151', fontSize: '13px', fontWeight: '500' }}>Applicants</span>
          </div>
          <h1 style={{ fontSize: '20px', fontWeight: '700', margin: 0, color: '#0F1E2E' }}>
            {job?.title || 'Applicants'}
          </h1>
        </div>
      </div>

      {/* Closed job banner */}
      {isJobClosed && (
        <div style={{
          backgroundColor: '#f3f4f6',
          color: '#6b7280',
          padding: '8px 16px',
          textAlign: 'center',
          borderRadius: '6px',
          marginBottom: '1rem',
          fontSize: '14px',
        }}>
          {job?.status === 'canceled'
            ? 'This job was canceled. Applicant actions are disabled.'
            : job?.status === 'expired'
              ? 'This job has expired. Applicant actions are disabled.'
              : 'This job is closed. Applicant actions are disabled.'}
        </div>
      )}

      {/* Poster Card */}
      {job && (
        <div style={{ backgroundColor: '#F8FAFC', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', padding: '1rem 1.25rem', marginBottom: '1.25rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
          <div>
            <div style={{ fontWeight: '600', fontSize: '14px', color: '#0F1E2E' }}>Job Poster</div>
            <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>
              {job.posterUrl ? 'PDF posters ready — Letter & A4' : 'Generate a printable poster with a QR code for this job.'}
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
            {job.posterUrl && (
              <>
                <a
                  href={job.posterUrl.letter + '?t=' + Date.now()} target="_blank" rel="noreferrer"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '6px 12px', borderRadius: '6px', border: '1px solid #d1d5db', backgroundColor: 'white', color: '#374151', fontSize: '13px', fontWeight: '500', textDecoration: 'none' }}
                >
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                  Letter
                </a>
                <a
                  href={job.posterUrl.a4 + '?t=' + Date.now()} target="_blank" rel="noreferrer"
                  style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '6px 12px', borderRadius: '6px', border: '1px solid #d1d5db', backgroundColor: 'white', color: '#374151', fontSize: '13px', fontWeight: '500', textDecoration: 'none' }}
                >
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                  A4
                </a>
              </>
            )}
            {job.secondLanguage && (
              <select
                value={posterLanguage}
                onChange={(e) => setPosterLanguage(e.target.value)}
                disabled={posterLoading}
                style={{ padding: '6px 10px', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '13px', color: '#374151', backgroundColor: 'white', cursor: 'pointer' }}
              >
                <option value="en">English</option>
                <option value={job.secondLanguage}>{LANG_LABELS[job.secondLanguage]}</option>
              </select>
            )}
            <button
              onClick={handleGeneratePoster}
              disabled={posterLoading}
              style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '6px 14px', borderRadius: '6px', border: 'none', backgroundColor: posterLoading ? '#9ca3af' : '#E8590C', color: 'white', fontSize: '13px', fontWeight: '500', cursor: posterLoading ? 'not-allowed' : 'pointer' }}
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="3" width="7" height="7" rx="1"/><rect x="14" y="3" width="7" height="7" rx="1"/>
                <rect x="14" y="14" width="7" height="7" rx="1"/><rect x="3" y="14" width="7" height="7" rx="1"/>
              </svg>
              {posterLoading ? 'Generating…' : job.posterUrl ? 'Regenerate' : 'Generate Poster'}
            </button>
          </div>
        </div>
      )}

      {/* Stats Bar */}
      <div style={{ padding: '0 0 1.25rem 0' }}>
        {isMobile ? (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '8px', backgroundColor: '#F8FAFC', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', padding: '0.75rem' }}>
            {[
              { label: 'Total', value: stats.total, color: '#6b7280' },
              { label: 'Pending', value: stats.pending, color: '#E8590C' },
              { label: 'Reviewing', value: stats.reviewing, color: '#0F1E2E' },
              { label: 'Send Offer', value: stats.send_offer, color: '#7c3aed' },
              { label: 'Hired', value: stats.hired, color: '#16A34A' },
              { label: 'Offer Sent', value: stats.offer_sent, color: '#0f766e' },
              { label: 'Rejected', value: stats.rejected, color: '#ef4444' },
            ].map((stat) => (
              <div key={stat.label} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '0.25rem 0' }}>
                <div style={{ fontSize: '18px', fontWeight: '700', color: stat.color, lineHeight: 1 }}>{stat.value}</div>
                <div style={{ fontSize: '10px', color: '#6b7280', textAlign: 'center', marginTop: '3px' }}>{stat.label}</div>
              </div>
            ))}
          </div>
        ) : (
          <div style={{ display: 'flex', gap: '0', flexWrap: 'wrap', backgroundColor: '#F8FAFC', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', padding: '0.75rem 1rem', alignItems: 'stretch' }}>
            {[
              { label: 'Total', value: stats.total, color: '#6b7280' },
              { label: 'Pending', value: stats.pending, color: '#E8590C' },
              { label: 'Reviewing', value: stats.reviewing, color: '#0F1E2E' },
              { label: 'Send Offer', value: stats.send_offer, color: '#7c3aed' },
              { label: 'Hired', value: stats.hired, color: '#16A34A' },
              { label: 'Offer Sent', value: stats.offer_sent, color: '#0f766e' },
              { label: 'Rejected', value: stats.rejected, color: '#ef4444' },
            ].map((stat, i) => (
              <React.Fragment key={stat.label}>
                {i > 0 && <div style={{ width: '1px', backgroundColor: '#e5e7eb', alignSelf: 'stretch', margin: '0 0.75rem' }} />}
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: '72px' }}>
                  <div style={{ fontSize: '20px', fontWeight: '700', color: stat.color, lineHeight: 1 }}>{stat.value}</div>
                  <div style={{ fontSize: '11px', color: '#6b7280', textAlign: 'center', marginTop: '3px' }}>{stat.label}</div>
                </div>
              </React.Fragment>
            ))}
          </div>
        )}
      </div>

      {/* Worker Heat Map — active jobs only */}
      {job?.status === 'active' && <WorkerHeatMap jobId={jobId} />}

      <div style={{ padding: '0' }}>

        {/* Applicants Table */}
        <div style={{ backgroundColor: '#F8FAFC', borderRadius: '8px', overflowX: 'auto', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          {applicants.length === 0 ? (
            <div style={{ padding: '3rem', textAlign: 'center', color: '#6b7280' }}>
              <p style={{ fontSize: '18px', marginBottom: '0.5rem' }}>No applicants yet</p>
              <p>Applications will appear here when workers apply to this job.</p>
            </div>
          ) : isMobile ? (
            /* Mobile: card layout */
            <div style={{ padding: '12px', display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {applicants.map((applicant) => (
                <div key={applicant.id} style={{ backgroundColor: 'white', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '16px' }}>
                  {/* Top row: avatar + name/email | status */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px', marginBottom: '12px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flex: 1, minWidth: 0 }}>
                      {applicant.userDetails?.photoURL ? (
                        <img src={applicant.userDetails.photoURL} alt="" style={{ width: '36px', height: '36px', borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
                      ) : (
                        <div style={{ width: '36px', height: '36px', borderRadius: '50%', backgroundColor: '#FF6B00', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontWeight: '600', fontSize: '16px', flexShrink: 0 }}>
                          {applicant.userDetails?.name?.charAt(0).toUpperCase() || 'U'}
                        </div>
                      )}
                      <div style={{ minWidth: 0 }}>
                        <div style={{ fontWeight: '600', fontSize: '14px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {renderApplicantName(applicant)}
                        </div>
                        <div style={{ fontSize: '12px', color: '#6b7280', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {applicant.userDetails?.email || 'No email'}
                        </div>
                        {i9RequestMap[applicant.userId]?.workbrightStatus === 'complete' && (
                          <span style={{ display: 'inline-block', marginTop: '2px', padding: '1px 6px', borderRadius: '10px', fontSize: '10px', fontWeight: 'bold', backgroundColor: '#f0fdf4', color: '#166534', border: '1px solid #bbf7d0' }}>
                            ✓ I-9 Verified
                          </span>
                        )}
                      </div>
                    </div>
                    {/* Status */}
                    <div style={{ flexShrink: 0 }}>
                      {(applicant.status === 'hired' || applicant.status === 'offer_sent') ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                          <span style={{ padding: '0.2rem 0.5rem', borderRadius: '4px', backgroundColor: applicant.status === 'hired' ? '#d1fae5' : '#ccfbf1', color: applicant.status === 'hired' ? '#065f46' : '#0f766e', fontSize: '11px', fontWeight: '600', border: '1px solid', borderColor: applicant.status === 'hired' ? '#6ee7b7' : '#5eead4' }}>
                            {applicant.status === 'hired' ? 'HIRED' : 'OFFER SENT'}
                          </span>
                          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                        </div>
                      ) : isJobClosed ? (
                        <span style={{ padding: '0.2rem 0.5rem', borderRadius: '4px', border: `1px solid ${getStatusColor(applicant.status)}`, backgroundColor: `${getStatusColor(applicant.status)}20`, color: getStatusColor(applicant.status), fontSize: '11px', fontWeight: '600' }}>
                          {applicant.status === 'pending' ? 'Pending' : applicant.status === 'reviewing' ? 'Reviewing' : applicant.status === 'schedule_interview' ? 'Schedule Interview' : applicant.status === 'send_offer' ? 'Send Offer' : applicant.status === 'rejected' ? (applicant.rejectedReason === 'job_canceled' ? 'Rejected (Job Canceled)' : 'Rejected') : applicant.status}
                        </span>
                      ) : (
                        <select
                          value={applicant.status}
                          onChange={(e) => handleStatusChange(applicant.id, e.target.value)}
                          disabled={updating === applicant.id}
                          style={{ padding: '0.2rem 0.4rem', borderRadius: '4px', border: `1px solid ${getStatusColor(applicant.status)}`, backgroundColor: `${getStatusColor(applicant.status)}20`, color: getStatusColor(applicant.status), fontSize: '12px', fontWeight: '600', cursor: updating === applicant.id ? 'not-allowed' : 'pointer' }}
                        >
                          <option value="pending">Pending</option>
                          <option value="reviewing">Reviewing</option>
                          <option value="schedule_interview">Schedule Interview</option>
                          <option value="send_offer">Send Offer</option>
                          <option value="offer_sent" disabled style={{ color: '#9ca3af' }}>Offer Sent</option>
                          <option value="hired" disabled style={{ color: '#9ca3af' }}>Hired</option>
                          <option value="rejected" disabled style={{ color: '#9ca3af' }}>Rejected</option>
                        </select>
                      )}
                    </div>
                  </div>
                  {/* AI Screening */}
                  <div style={{ marginBottom: '10px' }}>
                    <AiScoreCell applicant={applicant} />
                  </div>
                  {/* Applied date + message counts */}
                  <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '12px' }}>
                    {applicant.createdAt?.toDate().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    {' · '}↑ {applicant.messageCounts?.employer ?? 0} employer · ↓ {applicant.messageCounts?.worker ?? 0} worker
                  </div>
                  {/* References (HC-REFERENCES) */}
                  {applicant.userDetails?.references?.length > 0 && (
                    <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '12px' }}>
                      {applicant.userDetails.references.map((ref, i) => (
                        <div key={i}>{ref.name} — {ref.company} ({ref.relationship})</div>
                      ))}
                    </div>
                  )}
                  {/* Action buttons */}
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    <button
                      onClick={() => navigate(`/chat/${applicant.conversationId || applicant.id}`)}
                      style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '8px 12px', borderRadius: '6px', border: 'none', cursor: 'pointer', backgroundColor: '#8b5cf6', color: 'white', fontSize: '12px', fontWeight: '500' }}
                    >
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
                      Chat
                    </button>
                    {!isJobClosed && (applicant.status === 'hired' || applicant.status === 'offer_sent' || applicant.status === 'send_offer') && (
                      sentOffers[applicant.id] ? (
                        <button
                          onClick={() => { const url = sentOffers[applicant.id]; if (typeof url === 'string') window.open(url, '_blank'); }}
                          style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '8px 12px', borderRadius: '6px', border: 'none', cursor: typeof sentOffers[applicant.id] === 'string' ? 'pointer' : 'not-allowed', backgroundColor: '#0f766e', color: 'white', fontSize: '12px', fontWeight: '500', opacity: typeof sentOffers[applicant.id] === 'string' ? 1 : 0.5 }}
                        >
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                          View Offer
                        </button>
                      ) : (applicant.status === 'hired' || applicant.status === 'send_offer') && (
                        <button
                          onClick={() => openOfferModal(applicant.id)}
                          style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '8px 12px', borderRadius: '6px', border: 'none', cursor: 'pointer', backgroundColor: '#0f766e', color: 'white', fontSize: '12px', fontWeight: '500' }}
                        >
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>
                          Send Offer
                        </button>
                      )
                    )}
                    {!isJobClosed && applicant.status === 'hired' && i9RequestMap[applicant.userId] && (
                      <button
                        onClick={() => setI9Modal({ workerId: applicant.userId, i9RequestId: i9RequestMap[applicant.userId].id, workerName: applicant.userDetails?.name || 'Worker' })}
                        style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '8px 12px', borderRadius: '6px', border: 'none', cursor: 'pointer', backgroundColor: '#0369a1', color: 'white', fontSize: '12px', fontWeight: '500' }}
                      >
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
                        I-9
                      </button>
                    )}
                    {!isJobClosed && canRateApplicant(applicant) && (
                      <button
                        onClick={() => openRateModal(applicant)}
                        style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '8px 12px', borderRadius: '6px', border: 'none', cursor: 'pointer', backgroundColor: '#d97706', color: 'white', fontSize: '12px', fontWeight: '500' }}
                      >
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
                        Rate
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            /* Desktop: grid table */
            //
            // HC-RATING-004 desktop layout decision (flagged for review, not
            // decided silently — see hc-rating-004-employer-trigger-
            // investigation.md, which found this Actions column already
            // dense at 3 icons, unlike mobile's flexWrap card row): adding a
            // 4th bare-SVG icon (Rate, amber) in the same conditional-render
            // pattern as Chat/Offer/I-9, sized 18px vs. the other three's
            // 20px, plus a small complementary column-width bump
            // (Actions: 0.8fr -> 1fr in both the header and row grid
            // templates below) for breathing room. Rejected alternatives:
            // (b) a bigger structural column-ratio rework — unjustified,
            // since the 4-icon state is transient (only hired + not-yet-
            // rated + eligibility-window-open rows) and a rework would touch
            // every row in the table, not just that subset; (c) an overflow/
            // kebab menu — rejected for the same reasoning as mobile's
            // banner-vs-button decision (my_jobs_screen.dart): this is a
            // positive, time-limited call-to-action the product wants
            // noticed, not buried behind an extra click.
            <div style={{ width: '100%' }}>
              {/* Header */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: '2fr 1.2fr 1fr 1.2fr 1fr',
                backgroundColor: '#f9fafb',
                borderTop: '1px solid #e5e7eb',
                borderBottom: '1px solid #e5e7eb',
                padding: '0.75rem 1rem',
                gap: '0.5rem',
              }}>
                <div style={{ fontWeight: '600', fontSize: '14px', color: '#374151' }}>Applicant</div>
                <div style={{ fontWeight: '600', fontSize: '14px', color: '#374151' }}>AI Screening</div>
                <div style={{ fontWeight: '600', fontSize: '14px', color: '#374151' }}>Applied / Messages</div>
                <div style={{ fontWeight: '600', fontSize: '14px', color: '#374151' }}>Status</div>
                <div style={{ fontWeight: '600', fontSize: '14px', color: '#374151', textAlign: 'center' }}>Actions</div>
              </div>

              {/* Rows */}
              {applicants.map((applicant, index) => (
                <div key={applicant.id} style={{
                  display: 'grid',
                  gridTemplateColumns: '2fr 1.2fr 1fr 1.2fr 1fr',
                  borderBottom: '1px solid #e5e7eb',
                  padding: '1rem',
                  gap: '0.5rem',
                  alignItems: 'center',
                }}>

                  {/* Applicant */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    {applicant.userDetails?.photoURL ? (
                      <img src={applicant.userDetails.photoURL} alt="" style={{ width: '40px', height: '40px', borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} />
                    ) : (
                      <div style={{ width: '40px', height: '40px', borderRadius: '50%', backgroundColor: '#FF6B00', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontWeight: '600', fontSize: '18px', flexShrink: 0 }}>
                        {applicant.userDetails?.name?.charAt(0).toUpperCase() || 'U'}
                      </div>
                    )}
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontWeight: '500', fontSize: '14px' }}>
                        {renderApplicantName(applicant)}
                        {i9RequestMap[applicant.userId]?.workbrightStatus === 'complete' && (
                          <span style={{ display: 'inline-block', marginLeft: '8px', padding: '2px 8px', borderRadius: '12px', fontSize: '11px', fontWeight: 'bold', backgroundColor: '#f0fdf4', color: '#166534', border: '1px solid #bbf7d0', verticalAlign: 'middle' }}>
                            ✓ I-9 Verified
                          </span>
                        )}
                      </div>
                      <div style={{ fontSize: '13px', color: '#6b7280' }}>{applicant.userDetails?.email || 'No email'}</div>
                    </div>
                  </div>

                  {/* AI Screening */}
                  <div><AiScoreCell applicant={applicant} /></div>

                  {/* Applied / Messages */}
                  <div>
                    <div style={{ fontSize: '13px', color: '#374151' }}>
                      {applicant.createdAt?.toDate().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                    </div>
                    <div style={{ fontSize: '12px', color: '#9ca3af', marginTop: '2px' }}>
                      ↑ {applicant.messageCounts?.employer ?? 0} ↓ {applicant.messageCounts?.worker ?? 0}
                    </div>
                    {/* References (HC-REFERENCES) — nested in this cell, not a
                        new sibling, since this row is a 5-column CSS grid and
                        an extra top-level div would misalign every column after it. */}
                    {applicant.userDetails?.references?.length > 0 && (
                      <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                        {applicant.userDetails.references.map((ref, i) => (
                          <div key={i}>{ref.name} — {ref.company} ({ref.relationship})</div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Status */}
                  <div>
                    {(applicant.status === 'hired' || applicant.status === 'offer_sent') ? (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <span style={{
                          padding: '0.25rem 0.6rem',
                          borderRadius: '4px',
                          backgroundColor: applicant.status === 'hired' ? '#d1fae5' : '#ccfbf1',
                          color: applicant.status === 'hired' ? '#065f46' : '#0f766e',
                          fontSize: '12px',
                          fontWeight: '600',
                          border: '1px solid',
                          borderColor: applicant.status === 'hired' ? '#6ee7b7' : '#5eead4',
                        }}>
                          {applicant.status === 'hired' ? 'HIRED' : 'OFFER SENT'}
                        </span>
                        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                      </div>
                    ) : isJobClosed ? (
                      <span style={{ padding: '0.25rem 0.6rem', borderRadius: '4px', border: `1px solid ${getStatusColor(applicant.status)}`, backgroundColor: `${getStatusColor(applicant.status)}20`, color: getStatusColor(applicant.status), fontSize: '13px', fontWeight: '600' }}>
                        {applicant.status === 'pending' ? 'Pending' : applicant.status === 'reviewing' ? 'Reviewing' : applicant.status === 'schedule_interview' ? 'Schedule Interview' : applicant.status === 'send_offer' ? 'Send Offer' : applicant.status === 'rejected' ? (applicant.rejectedReason === 'job_canceled' ? 'Rejected (Job Canceled)' : 'Rejected') : applicant.status}
                      </span>
                    ) : (
                      <select
                        value={applicant.status}
                        onChange={(e) => handleStatusChange(applicant.id, e.target.value)}
                        disabled={updating === applicant.id}
                        style={{
                          padding: '0.25rem 0.5rem',
                          borderRadius: '4px',
                          border: `1px solid ${getStatusColor(applicant.status)}`,
                          backgroundColor: `${getStatusColor(applicant.status)}20`,
                          color: getStatusColor(applicant.status),
                          fontSize: '13px',
                          fontWeight: '600',
                          cursor: updating === applicant.id ? 'not-allowed' : 'pointer',
                          width: '100%',
                        }}
                      >
                        <option value="pending">Pending</option>
                        <option value="reviewing">Reviewing</option>
                        <option value="schedule_interview">Schedule Interview</option>
                        <option value="send_offer">Send Offer</option>
                        <option value="offer_sent" disabled style={{ color: '#9ca3af' }}>Offer Sent (system)</option>
                        <option value="hired" disabled style={{ color: '#9ca3af' }}>Hired (system)</option>
                        <option value="rejected" disabled style={{ color: '#9ca3af' }}>Rejected (system)</option>
                      </select>
                    )}
                  </div>

                  {/* Actions — icon only */}
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '14px' }}>

                    {/* Chat icon */}
                    <div
                      onClick={() => navigate(`/chat/${applicant.conversationId || applicant.id}`)}
                      title="Open Chat"
                      style={{ cursor: 'pointer', color: '#8b5cf6' }}
                    >
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                      </svg>
                    </div>

                    {/* Offer Letter icon */}
                    {!isJobClosed && (applicant.status === 'hired' || applicant.status === 'offer_sent' || applicant.status === 'send_offer') && (
                      sentOffers[applicant.id] ? (
                        <div
                          onClick={() => { const url = sentOffers[applicant.id]; if (typeof url === 'string') window.open(url, '_blank'); }}
                          title="View Offer Letter"
                          style={{ cursor: typeof sentOffers[applicant.id] === 'string' ? 'pointer' : 'not-allowed', color: '#0f766e', opacity: typeof sentOffers[applicant.id] === 'string' ? 1 : 0.5 }}
                        >
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                            <polyline points="14 2 14 8 20 8"/>
                            <line x1="16" y1="13" x2="8" y2="13"/>
                            <line x1="16" y1="17" x2="8" y2="17"/>
                            <polyline points="10 9 9 9 8 9"/>
                          </svg>
                        </div>
                      ) : (applicant.status === 'hired' || applicant.status === 'send_offer') && (
                        <div
                          onClick={() => openOfferModal(applicant.id)}
                          title="Send Offer Letter"
                          style={{ cursor: 'pointer', color: '#0f766e' }}
                        >
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                            <polyline points="14 2 14 8 20 8"/>
                            <line x1="12" y1="18" x2="12" y2="12"/>
                            <line x1="9" y1="15" x2="15" y2="15"/>
                          </svg>
                        </div>
                      )
                    )}

                    {/* I-9 Schedule icon */}
                    {!isJobClosed && applicant.status === 'hired' && i9RequestMap[applicant.userId] && (
                      <div
                        onClick={() => setI9Modal({ workerId: applicant.userId, i9RequestId: i9RequestMap[applicant.userId].id, workerName: applicant.userDetails?.name || 'Worker' })}
                        title="Schedule I-9"
                        style={{ cursor: 'pointer', color: '#0369a1' }}
                      >
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <rect x="3" y="4" width="18" height="18" rx="2" ry="2"/>
                          <line x1="16" y1="2" x2="16" y2="6"/>
                          <line x1="8" y1="2" x2="8" y2="6"/>
                          <line x1="3" y1="10" x2="21" y2="10"/>
                        </svg>
                      </div>
                    )}

                    {/* Rate Worker icon — HC-RATING-004. Sized down (18px vs
                        the other three icons' 20px) as this row's 4th icon —
                        see the design-decision note above the Actions column
                        header for the layout tradeoff this was weighed
                        against. */}
                    {!isJobClosed && canRateApplicant(applicant) && (
                      <div
                        onClick={() => openRateModal(applicant)}
                        title="Rate Worker"
                        style={{ cursor: 'pointer', color: '#d97706' }}
                      >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/>
                        </svg>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── Send Offer / Re-offer Modal ──────────────────────────────────── */}
      {offerWarningModal && (
        <div style={{
          position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 1000, padding: '1rem'
        }}>
          <div style={{
            backgroundColor: 'white', borderRadius: '8px', width: '100%',
            maxWidth: '460px', padding: '2rem', boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
          }}>
            {offerWarningModal.currentStatus === 'rejected' ? (
              /* ── Rejected → send_offer: re-offer flow ── */
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '1rem' }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
                  <h2 style={{ fontSize: '20px', fontWeight: 'bold', margin: 0 }}>
                    {offerWarningModal.rejectedReason === 'Candidate declined offer'
                      ? 'Re-offer to Candidate Who Declined'
                      : 'Re-offer to Rejected Candidate'}
                  </h2>
                </div>
                {offerWarningModal.rejectedReason === 'Candidate declined offer' ? (
                  <p style={{ color: '#374151', marginBottom: '1rem' }}>
                    This candidate previously declined your offer. You must provide a reason for re-offering before continuing.
                  </p>
                ) : (
                  <p style={{ color: '#374151', marginBottom: '1rem' }}>
                    You are re-opening this application for an offer. You may optionally provide a reason.
                  </p>
                )}
                <div style={{ marginBottom: '1.25rem' }}>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '0.375rem' }}>
                    {offerWarningModal.rejectedReason === 'Candidate declined offer'
                      ? 'Reason for re-offering *'
                      : 'Reason for re-offering (optional)'}
                  </label>
                  <textarea
                    value={resendReason}
                    onChange={e => setResendReason(e.target.value)}
                    placeholder={offerWarningModal.rejectedReason === 'Candidate declined offer'
                      ? 'e.g. Offering a higher salary or adjusted start date'
                      : 'Optional — leave blank to re-offer without a reason'}
                    rows={3}
                    style={{
                      width: '100%', boxSizing: 'border-box', padding: '8px 12px',
                      fontSize: '14px', border: '1px solid #cbd5e0', borderRadius: '6px',
                      color: '#1a202c', resize: 'vertical', fontFamily: 'inherit'
                    }}
                  />
                </div>
                {resendOfferError && (
                  <p style={{ color: '#dc2626', fontSize: '14px', marginBottom: '1rem' }}>
                    {resendOfferError}
                  </p>
                )}
                <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
                  <button
                    onClick={() => { setOfferWarningModal(null); setResendOfferError(null); }}
                    style={{
                      padding: '0.625rem 1.25rem', borderRadius: '6px',
                      border: '1px solid #d1d5db', backgroundColor: 'white',
                      color: '#374151', cursor: 'pointer', fontWeight: '500'
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={confirmSendOffer}
                    disabled={offerWarningModal.rejectedReason === 'Candidate declined offer' && !resendReason.trim()}
                    style={{
                      padding: '0.625rem 1.25rem', borderRadius: '6px',
                      border: 'none', backgroundColor: '#E8590C',
                      color: 'white', cursor: (offerWarningModal.rejectedReason === 'Candidate declined offer' && !resendReason.trim()) ? 'not-allowed' : 'pointer',
                      fontWeight: '600',
                      opacity: (offerWarningModal.rejectedReason === 'Candidate declined offer' && !resendReason.trim()) ? 0.5 : 1
                    }}
                  >
                    Confirm — Re-offer
                  </button>
                </div>
              </>
            ) : (
              /* ── Non-rejected → send_offer: standard warning ── */
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', margin: '0 0 1rem 0' }}>
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#f59e0b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
                  <h2 style={{ fontSize: '20px', fontWeight: 'bold', margin: 0 }}>Send Offer Letter</h2>
                </div>
                <p style={{ color: '#374151', marginBottom: '1rem' }}>
                  Before sending, please note:
                </p>
                <ul style={{ color: '#374151', paddingLeft: '1.25rem', marginBottom: '1.5rem', lineHeight: '1.75' }}>
                  <li>Once the offer letter is sent, this application will be locked.</li>
                  <li>The worker will receive the offer via chat and email.</li>
                  <li>If the worker declines, the job will automatically reopen.</li>
                </ul>
                <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'flex-end' }}>
                  <button
                    onClick={() => setOfferWarningModal(null)}
                    style={{
                      padding: '0.625rem 1.25rem', borderRadius: '6px',
                      border: '1px solid #d1d5db', backgroundColor: 'white',
                      color: '#374151', cursor: 'pointer', fontWeight: '500'
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={confirmSendOffer}
                    style={{
                      padding: '0.625rem 1.25rem', borderRadius: '6px',
                      border: 'none', backgroundColor: '#E8590C',
                      color: 'white', cursor: 'pointer', fontWeight: '600'
                    }}
                  >
                    Confirm — Send Offer
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* ── I-9 Scheduling Modal ───────────────────────────────────────── */}
      {i9Modal && (
        <div style={{
          position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 1000, padding: '1rem'
        }}>
          <div style={{
            backgroundColor: 'white', borderRadius: '8px', width: '100%',
            maxWidth: '420px', padding: '2rem', boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h2 style={{ fontSize: '18px', fontWeight: 'bold', margin: 0 }}>
                I-9 Appointment — {i9Modal.workerName}
              </h2>
              <button
                onClick={() => setI9Modal(null)}
                style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: '#6b7280' }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>
            <I9DatePicker
              i9RequestId={i9Modal.i9RequestId}
              onScheduled={() => setI9Modal(null)}
            />
          </div>
        </div>
      )}

      {/* ── Rate Worker Modal (HC-RATING-004) ────────────────────────────── */}
      {rateModal && (
        <div style={{
          position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 1000, padding: '1rem'
        }}>
          <div style={{
            backgroundColor: 'white', borderRadius: '8px', width: '100%',
            maxWidth: '460px', padding: '2rem', boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '0.5rem' }}>
              <button
                onClick={() => setRateModal(null)}
                style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: '#6b7280' }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>
            <RateWorkerModal
              applicationId={rateModal.applicationId}
              workerName={rateModal.workerName}
              onSuccess={handleRatingSuccess}
              onClose={() => setRateModal(null)}
            />
          </div>
        </div>
      )}

      {/* ── Offer Letter Modal ──────────────────────────────────────────── */}
      {offerModal && (
        <div style={{
          position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          zIndex: 1000, padding: '1rem'
        }}>
          <div style={{
            backgroundColor: 'white', borderRadius: '8px', width: '100%',
            maxWidth: '560px', maxHeight: '90vh', overflowY: 'auto',
            padding: '2rem', boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h2 style={{ fontSize: '20px', fontWeight: 'bold', margin: 0 }}>Send Offer Letter</h2>
              <button
                onClick={() => setOfferModal(null)}
                style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: '#6b7280' }}
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
              </button>
            </div>

            {offerModal.sent ? (
              <div style={{ textAlign: 'center', padding: '2rem 0' }}>
                <div style={{ width: '56px', height: '56px', borderRadius: '50%', backgroundColor: '#dcfce7', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 1rem' }}>
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#16A34A" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                </div>
                <p style={{ fontSize: '18px', fontWeight: '600', color: '#166534' }}>Offer letter sent successfully!</p>
                <button
                  onClick={() => setOfferModal(null)}
                  style={{
                    marginTop: '1.5rem', backgroundColor: '#E8590C', color: 'white',
                    padding: '0.625rem 1.5rem', borderRadius: '6px', border: 'none',
                    cursor: 'pointer', fontWeight: '600'
                  }}
                >
                  Close
                </button>
              </div>
            ) : offerModal.loading && !offerModal.fields ? (
              <div style={{ textAlign: 'center', padding: '2rem 0', color: '#6b7280' }}>
                Generating offer letter draft…
              </div>
            ) : (
              <>
                {offerModal.error && (
                  <div style={{
                    backgroundColor: '#fee2e2', color: '#991b1b', padding: '0.75rem 1rem',
                    borderRadius: '6px', marginBottom: '1rem', fontSize: '14px'
                  }}>
                    {offerModal.error}
                  </div>
                )}

                {[
                  { label: 'Job Title', field: 'jobTitle' },
                  { label: 'Company', field: 'company' },
                  { label: 'Salary / Compensation', field: 'salary' },
                  { label: 'Location', field: 'location' },
                  { label: 'Employer Name', field: 'employerName' },
                  { label: 'Worker Name', field: 'workerName' },
                ].map(({ label, field }) => (
                  <div key={field} style={{ marginBottom: '1rem' }}>
                    <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '0.375rem' }}>
                      {label}
                    </label>
                    <input
                      type="text"
                      value={offerModal.fields?.[field] ?? ''}
                      onChange={(e) => updateOfferField(field, e.target.value)}
                      style={{
                        width: '100%', padding: '0.5rem 0.75rem', borderRadius: '6px',
                        border: '1px solid #d1d5db', fontSize: '14px', boxSizing: 'border-box'
                      }}
                    />
                  </div>
                ))}

                <div style={{ marginBottom: '1rem' }}>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '0.375rem' }}>
                    Start Date <span style={{ color: '#ef4444' }}>*</span>
                  </label>
                  <input
                    type="datetime-local"
                    min={(() => {
                      const now = new Date(Date.now() + 60 * 1000);
                      const pad = (n) => String(n).padStart(2, '0');
                      return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T${pad(now.getHours())}:${pad(now.getMinutes())}`;
                    })()}
                    value={offerModal.fields?.startDate ?? ''}
                    onChange={(e) => updateOfferField('startDate', e.target.value)}
                    style={{
                      width: '100%', padding: '8px 12px', borderRadius: '6px',
                      border: `1px solid ${offerModal.fields?.startDate ? '#cbd5e0' : '#ef4444'}`,
                      fontSize: '14px', color: '#1a202c', boxSizing: 'border-box'
                    }}
                  />
                </div>

                <div style={{ marginBottom: '1rem' }}>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '0.375rem' }}>
                    End Date <span style={{ color: '#6b7280', fontWeight: '400' }}>(or TBD)</span>
                  </label>
                  <input
                    type="datetime-local"
                    min={offerModal.fields?.startDate || undefined}
                    value={offerModal.fields?.endDate && offerModal.fields.endDate !== 'TBD' ? offerModal.fields.endDate : ''}
                    onChange={(e) => updateOfferField('endDate', e.target.value)}
                    style={{
                      width: '100%', padding: '8px 12px', borderRadius: '6px',
                      border: '1px solid #cbd5e0', fontSize: '14px', color: '#1a202c',
                      boxSizing: 'border-box'
                    }}
                  />
                  <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '0.375rem' }}>
                    Leave blank to send as TBD. The worker will be notified when the position ends.
                  </p>
                  {offerDateIssue && offerModal.fields?.startDate?.trim() && (
                    <p role="alert" style={{ fontSize: '13px', color: '#b91c1c', marginTop: '0.5rem' }}>
                      {OFFER_DATE_MESSAGES[offerDateIssue]}
                    </p>
                  )}
                </div>

                <div style={{ marginBottom: '1.5rem' }}>
                  <label style={{ display: 'block', fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '0.375rem' }}>
                    Additional Terms <span style={{ color: '#9ca3af', fontWeight: '400' }}>(optional)</span>
                  </label>
                  <textarea
                    rows={4}
                    value={offerModal.fields?.additionalTerms ?? ''}
                    onChange={(e) => updateOfferField('additionalTerms', e.target.value)}
                    placeholder="Any additional terms, conditions, or notes…"
                    style={{
                      width: '100%', padding: '0.5rem 0.75rem', borderRadius: '6px',
                      border: '1px solid #d1d5db', fontSize: '14px', boxSizing: 'border-box',
                      resize: 'vertical'
                    }}
                  />
                </div>

                <button
                  onClick={sendOffer}
                  disabled={offerSendBlocked}
                  style={{
                    width: '100%', backgroundColor: '#E8590C', color: 'white',
                    padding: '0.75rem', borderRadius: '6px', border: 'none',
                    cursor: offerSendBlocked ? 'not-allowed' : 'pointer',
                    opacity: offerSendBlocked ? 0.5 : 1,
                    fontWeight: '600', fontSize: '15px'
                  }}
                >
                  {offerModal.loading ? 'Sending…' : 'Send Offer Letter'}
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function AiScoreCell({ applicant }) {
  const { aiScore, aiLabel, aiSummary } = applicant;

  if (aiScore == null) {
    return (
      <span style={{ color: '#9ca3af', fontStyle: 'italic', fontSize: '13px' }}>
        Screening pending…
      </span>
    );
  }

  const chipStyle = (() => {
    if (aiLabel === 'strong') {
      return { backgroundColor: '#dcfce7', color: '#166534', borderColor: '#bbf7d0' };
    }
    if (aiLabel === 'weak') {
      return { backgroundColor: '#fee2e2', color: '#991b1b', borderColor: '#fecaca' };
    }
    return { backgroundColor: '#fef3c7', color: '#92400e', borderColor: '#fde68a' };
  })();

  return (
    <div>
      <div style={{
        display: 'inline-block',
        padding: '0.25rem 0.625rem',
        borderRadius: '9999px',
        border: '1px solid',
        fontSize: '12px',
        fontWeight: '600',
        marginBottom: aiSummary ? '0.375rem' : 0,
        ...chipStyle,
      }}>
        {aiLabel} — {aiScore}/100
      </div>
      {aiSummary && (
        <div style={{ fontSize: '12px', color: '#6b7280', maxWidth: '260px', lineHeight: '1.4' }}>
          {aiSummary}
        </div>
      )}
    </div>
  );
}

export default ViewApplicantsPage;